import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import InputMask from "react-input-mask";
import { joiResolver } from '@hookform/resolvers/joi';
//import moment from 'moment';
//import 'moment-timezone';
import moment from "moment-timezone";
import BasicDatePicker from "../formElements/DatePicker";
import AddressLookup from "../formElements/AddressLookup";
import CompanyLookup from "../formElements/CompanyLookup";
import CPLookup from "../formElements/CPLookup";
import states from "../formElements/AddressLookup/states";
// import { Error } from "../../ui/Toast";

const Joi = require('joi').extend(require('@joi/date'));

const schema = Joi.object({
  company: Joi.any(),
  company_name: Joi.any(),
  installer_phone: Joi.any(),
  installer_address: Joi.any(),
  installer_address_address1: Joi.any(),
  installer_address_address2: Joi.any(),
  installer_address_city: Joi.any(),
  installer_address_state: Joi.any(),
  installer_address_zip: Joi.any(),
  homeowner_address_address1: Joi.any(),
  homeowner_address_address2: Joi.any(),
  homeowner_address_city: Joi.any(),
  homeowner_address_state: Joi.any(),
  homeowner_address_zip: Joi.any(),
  cp_number: Joi.any(),
  image1File: Joi.any(),
  image2File: Joi.any(),
  image3File: Joi.any(),
  image1: Joi.any().required()
    .messages({
      "any.required": "At least one image is required",
    }),
  image2: Joi.any(),
  image3: Joi.any(),
  installation_date: Joi.date()
    .max('now')
    .format("MM/DD/YYYY")
    .messages({
        "date.max": "Please enter a valid Installation Date",
        "date.format": "Please enter a valid Installation Date"
    }),
  homeowner_firstname: Joi.string()
    .required()
    .messages({
        "any.required": "Please enter Homeowner First Name",
        "string.empty": "Please enter Homeowner First Name",
    }),
  homeowner_lastname: Joi.string()
    .required()
    .messages({
      "any.required": "Please enter Homeowner Last Name",
      "string.empty": "Please enter Homeowner Last Name",
    }),
  homeowner_email: Joi.string().email({ tlds: { allow: false } })
    .messages({
      "any.required": "Please enter a valid email address",
      "string.empty": "Please enter a valid email address",
      "string.email": "Please enter a valid email address",
    }),
  homeowner_address: Joi.any().required()
    .messages({
      "any.required": "Please enter an address",
    }),
  homeowner_phone: Joi.any()
});

const theme = createTheme({
  palette: {
    textError: '#d32f2f',
  },
  typography: {
    // In Chinese and Japanese the characters are usually larger,
    // so a smaller fontsize may be appropriate.
    fontSize: 20,
  },
});

// POST method implementation:
async function postData(url = '', data = {}) {
  // Default options are marked with *
  const response = await fetch(url, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    // mode: 'no-cors', // no-cors, *cors, same-origin
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  // console.log('postData response', response);

  return response.json(); // parses JSON response into native JavaScript objects
}

const baseAPIURL = process.env.REACT_APP_API_URL;
const localAPIURL = process.env.REACT_APP_LOCAL_API_URL;

const registrationAPIEndpoint = '/Boiler/CreateBoilerRegistration';

function ProductRegistrationForm() {
  const {
    control,
    formState: { errors },
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    watch
    // formState: { errors },
  } = useForm({
    defaultValues: {
      "installation_date": moment().format("MM/DD/YYYY")
    },
    mode: "onSubmit",
    resolver: joiResolver(schema),
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });

  const [isHomeowner, setIsHomeowner] = useState(null);
  const [isInstaller, setIsInstaller] = useState(null);
  const [isInstallerInfo, setIsInstallerInfo] = useState(null);
  const [showProduct, setShowProduct] = useState(false);
  const [showCompanyName, setShowCompanyName] = useState(false);
  const [cpInfo, setCpInfo] = useState(null);
  // const [imageStrings, setImageStrings] = useState({});
  const [isLoading, setIsLoading] = useState(null);
  const [success, setSuccess] = useState(null);

  // const handleCompanyChange = (event) => {
  //   console.log('company event', event);
  // }
  const companyValue = watch('company');
  const installationDate = watch('installation_date');
  // console.log('installationDate', installationDate);
  const companyAddressValue = watch('installer_address');
  const homeownerAddressValue = watch('homeowner_address');
  const installerAddressValue = watch('installer_address');
  const cpValue = watch('cp_number');
  const watchedValues = watch();

  useEffect(() => {
    // console.log('companyValue', companyValue);
    if (companyValue?.value?.address) {
      setValue('installer_address', companyValue.value?.address);
    }
    else if (companyValue?.value?.otherCompany) {
      setValue("installer_address", null);
      setValue("company_name", companyValue?.value?.otherCompany);
      setShowCompanyName(true);
    }

    return () => {

    }
  }, [companyValue]);

  useEffect(() => {
    //console.log('companyAddressValue', companyAddressValue);
    // if (companyAddressValue?.value?.address) {
    //   setValue('installer_address', companyValue.value?.address);
    // }
    if (companyAddressValue?.value?.customAddress) {
      setValue('installer_address_address1', companyAddressValue?.value?.address1)
      setValue("installer_address", null);
    }

    return () => {

    }
  }, [companyAddressValue]);

  const getStateValue = (adminDistrict) => {
    //console.log('getStateValue adminDistrict', adminDistrict);
    const stateValue = states.find((item) => {
      // console.log('getStateValue item', item);
      if ([item.name, item.abbreviation].includes(adminDistrict)) {
        return true;
      }
      return false;
    });
    return stateValue?.name || adminDistrict || '';
  }

  useEffect(() => {
    //console.log('homeownerAddressValue', homeownerAddressValue);
    const {
      addressLine2,
      adminDistrict,
      locality,
      postalCode
    } = homeownerAddressValue?.value?.address || {};
    const stateValue = getStateValue(adminDistrict);
    setValue("homeowner_address_address2", addressLine2);
    setValue("homeowner_address_city", locality);
    setValue("homeowner_address_state", stateValue);
    setValue("homeowner_address_zip", postalCode);

    return () => {

    }
  }, [homeownerAddressValue]);

  useEffect(() => {
    //console.log('installerAddressValue', installerAddressValue);
    const {
      addressLine2,
      adminDistrict,
      locality,
      postalCode
    } = installerAddressValue?.value?.address || {};
    const stateValue = getStateValue(adminDistrict);
    setValue("installer_address_address2", addressLine2);
    setValue("installer_address_city", locality);
    setValue("installer_address_state", stateValue);
    setValue("installer_address_zip", postalCode);

    return () => {

    }
  }, [installerAddressValue]);

  useEffect(() => {
    const formattedDate = moment(installationDate).format('MM/DD/YYYY');
    setValue('installation_date', formattedDate);

    return () => {

    }
  }, [installationDate])

  useEffect(() => {
    // console.log('companyValue', companyValue);

    const cpInfoData = [];

    const {
      cpNumberId,
      installationDate: cpInstallationDate,
      itemDescription,
      model,
      series,
      size,
      manufactureDate,
      productRegistrationDate
    } = cpValue?.value || {};

    // console.log('cpValue', cpValue)

    // console.log('cpInstallationDate', cpInstallationDate);

    if (cpNumberId) {
      if (productRegistrationDate) {
        setError(
          'cp_number',
          {
            type: 'custom',
            message: 'This product has already been registered.'
          }
        );
      }
      if (
        !productRegistrationDate &&
        cpInstallationDate &&
        (cpInstallationDate !== "0001-01-01T00:00:00")
      ) {
        setError(
          'cp_number',
          {
            type: 'custom',
            message: `${cpValue.title} can not be registered online.
            Apologies for inconvenience. Please contact Weil-McLain`
          }
        );
      }
      if (
        !productRegistrationDate &&
        (!cpInstallationDate || cpInstallationDate === "0001-01-01T00:00:00")
      ) {
        clearErrors('cp_number')
      }
      if(itemDescription) {
        cpInfoData.push({
          label: "Description",
          value: itemDescription
        })
      }
      if (model?.modelName) {
        cpInfoData.push({
          label: "Model",
          value: model?.modelName
        })
      }
      if (series?.seriesName) {
        cpInfoData.push({
          label: "Series",
          value: series?.seriesName
        })
      }
      if (size?.sizeName) {
        cpInfoData.push({
          label: "Size",
          value: size?.sizeName
        })
      }
      if (manufactureDate) {
        cpInfoData.push({
          label: "Manufacture Date",
          //value: moment(manufactureDate).calendar()
          value: moment(manufactureDate).tz("Africa/Johannesburg").format("MM/DD/YYYY")
        })
      }
    }

    setCpInfo(cpInfoData);

    return () => {

    }
  }, [cpValue])

  // useEffect(() => {
  //   // Show all errors as Toastify
  //   console.log(Object.entries(errors));
  //   Object.entries(errors).map((error) => (
  //       Error({
  //           message: error[1].message,
  //           toastId: error[0]
  //       })
  //   ))
  // },[errors]);

  const getBase64 = (file) => (
    new Promise(resolve => {
      // let fileInfo;
      let baseURL = "";
      // Make new FileReader
      const reader = new FileReader();

      // Convert the file to base64 text
      reader.readAsDataURL(file);

      // on reader load somthing...
      reader.onload = () => {
        // Make a fileInfo Object
        // console.log("Called", reader);
        baseURL = reader.result;
        // console.log(baseURL);
        resolve(baseURL);
      };
      // console.log('fileInfo', fileInfo);
    })
  );

  const handleImageChange = (event) => {
    // console.log('handleImage event', event);
    const { files, name } = event.target;
    // const { files } = event.target;
    // console.log('image files', files);

    if (files[0]) {
      // const reader = new FileReader();
      const file = files[0];
      const fieldName = name.replace("File", "");

      getBase64(file)
      .then(result => {
        file.base64 = result;
        // console.log("File Is", file);

        const base64Data = file.base64.split(';');

        // console.log('base64Data', base64Data);
        const trimmedBase64 = base64Data[1].replace('base64,', '');

        const imageData = {
          "Filename": file.name,
          "MimeType": file.type,
          "Base64String": trimmedBase64
        }

        setValue(fieldName, imageData);

        // this.setState({
        //   base64URL: result,
        //   file
        // });
      })
      .catch(err => {
        //console.log(err);
      });
      // reader.readAsDataURL(files[0]);

      // const imageData = {
      //   "Filename": files[0].name,
      //   "MimeType": files[0].type
      // }

      // reader.onload = (e) => {
      //   const fieldName = name.replace("File", "");
      //   console.log('readImage', e);
      //   const imageString = e.target.result;
      //   // console.log('imageString', imageString);

      //   imageData.Base64String = imageString;
      //   setValue(fieldName, imageString);
      // }
    }


  }

  const onSubmit = (data) => {
    setIsLoading(true);
    //console.log('form data', data);

    const cleanHomeownerPhone = data?.homeowner_phone.replaceAll('-', '');
    const cleanHomeownerPhoneValid = cleanHomeownerPhone.match(/\d/g).length === 10;
    if (!cleanHomeownerPhoneValid) {
      setError(
        'homeowner_phone',
        {
          type: 'custom',
          message: 'Please enter a 10-digit phone number'
        }
      );
      setIsLoading(false);
      return;
    }

    // const installerAddress = data.installer_address?.value?.address;
    // console.log('installerAddress', installerAddress);

    const { image1, image2, image3 } = data;

    var init_img__arr = [image1, image2, image3];
    var filtered_imgs = init_img__arr.filter(function (el) {
      return el != '';
    });

    const imageRequests = filtered_imgs.map((image) => {
      // const key = Number(index + 1);
      const imageAPIEndpoint = '/ImageUpload/UploadContentByBase64String';

      const imageRequestData = {
        "ContainerName": "3c3bfcdf-94cc-e911-a97f-000d3a1d5285",
        "FileInfo": [image]
      }

      const imageAPIData = {
        "method": "POST",
        "baseURL": baseAPIURL,
        "endpoint": imageAPIEndpoint,
        "apiData": imageRequestData
      }

      //console.log("imageRequest API Data", imageAPIData);
      if ( ! imageRequestData["FileInfo"] ) {
        return null;
      } else{
        return postData(localAPIURL, imageAPIData);
      }
    });

    // console.log('data.cp_number?.value', data.cp_number?.value);

    if (data.cp_number?.value?.productRegistrationDate) {
      setError(
        'cp_number',
        {
          type: 'custom',
          message: 'This product has already been registered'
        }
      );
      setIsLoading(false);
      return;
    }

    if (
      !data.cp_number?.value?.productRegistrationDate &&
      data.cp_number?.value?.installationDate
    ) {
      setError(
        'cp_number',
        {
          type: 'custom',
          message: `${data.cp_number?.title} can not be registered online.
          Apologies for inconvenience. Please contact Weil-McLain`
        }
      );
      setIsLoading(false);
      return;
    }

    // console.log("CPNumberData", CPNumberData);

    const homeownerAddress = {
      addressLine: data.homeowner_address_address1,
      addressLine2: data.homeowner_address_address2,
      locality: data.homeowner_address_city,
      adminDistrict: data.homeowner_address_state,
      countryRegion: "United States",
      postalCode: data.homeowner_address_zip
    };

    const preparedData = {
      "HomeownerName": `${data.homeowner_firstname}`,
      "HomeownerLastName": `${data.homeowner_lastname}`,
      "HomeownerMobile": cleanHomeownerPhone,
      "HomeownerEmail": data.homeowner_email,
      "HomeownerAddressLine1": homeownerAddress?.addressLine,
      "HomeownerAddressLine2": homeownerAddress?.addressLine2,
      "HomeownerCity": homeownerAddress?.locality,
      "HomeownerStateOrProvince": homeownerAddress?.adminDistrict,
      "Homeownercounty": homeownerAddress?.adminDistrict2,
      "HomeownerCountry": homeownerAddress?.countryRegion,
      "HomeownerPostalCode": homeownerAddress?.postalCode,
      "IsSubmitted": true,
    };

    const accountId = data.company?.value?.accountId;

    if (accountId) {
      preparedData.Account = accountId;
    }
    else {
      preparedData.CompanyName = data.company_name;

      let companyAddress;
      if (data.installer_address?.value?.address) {
        companyAddress = data.installer_address?.value?.address;
      }
      else if (data.installer_address_address1) {
        companyAddress = {
          addressLine: data.installer_address_address1,
          addressLine2: data.installer_address_address2,
          locality: data.installer_address_city,
          adminDistrict: data.installer_address_state,
          postalCode: data.installer_address_zip,
          countryRegion: "United States"
        }
      }

      preparedData.CompanyAddressLine1 = companyAddress?.addressLine;
      preparedData.CompanyAddressLine2 = companyAddress?.addressLine2;
      preparedData.CompanyCity = companyAddress?.locality;
      preparedData.CompanyState = companyAddress?.adminDistrict;
      preparedData.CompanyZip = companyAddress?.postalCode;
      preparedData.CompanyCountry = companyAddress?.countryRegion;
    }

    // if (data?.installer_phone) {
    //   const cleanInstallerPhone = data?.installer_phone.replaceAll('-', '');
    //   const cleanInstallerPhoneValid = cleanInstallerPhone.match(/\d/g).length === 10;
    //   if (!cleanInstallerPhoneValid) {
    //     setError(
    //       'installer_phone',
    //       {
    //         type: 'custom',
    //         message: 'Please enter a 10-digit phone number'
    //       }
    //     );
    //     setIsLoading(false);
    //     return;
    //   }

    //   preparedData.BusinessPhoneNumber = cleanInstallerPhone;
    // }

    // setSuccess(false);
    // setIsLoading(false);

    const CPNumberData = {
      "CPNumber": data.cp_number?.value?.cpNumberId,
      "InstallationDate": moment(data.installation_date).format("MM/DD/YYYY")
    }

    // Submit all images and get response
    Promise.all(imageRequests)
      .then(imageResponses => {
        //console.log('imageResponses', imageResponses);

        imageResponses.forEach((item, index) => {
          const key = Number(index + 1);
          CPNumberData[`image${key}`] = item[0]?.cdnUrl;
        })

        preparedData.CPNumbers = [CPNumberData];

        const apiData = {
          "method": "POST",
          "baseURL": baseAPIURL,
          "endpoint": registrationAPIEndpoint,
          "apiData": preparedData
        }

        // console.log('submitting apiData', apiData);

        // Now submit the BoilerRegistration
        postData(localAPIURL, apiData)
          .then(registrationResponse => {
            // console.log('registrationResponse data', registrationResponse);
            if (registrationResponse.responsePreamble?.responseStatus === "Success") {
              setSuccess(true);
            }
            else {
              setSuccess(false);
            }
            setIsLoading(false);
          })
          .catch((error) => {
            //console.log('registrationResponse error', error);
            setSuccess(false);
            setIsLoading(false);
          });

    })
  };

  function handleKeyDown(event) {
    if (event.keyCode === 13 ) {
      event.preventDefault();
      // if ( !document.getElementById("xyz").focus() ) {
      //   event.preventDefault();
      // } else {
      //   console.log('pressed enter');
      // }
    }
  }

  // console.log(watch("example")); // watch input value by passing the name of it
  // console.log('isHomeowner', isHomeowner);
  // console.log('isInstallerInfo', isInstallerInfo);

  // const installerAddress = useWatch({
  //   control,
  //   name: "installer_address",
  // });

  //console.log('watchedValues', watchedValues);
  //console.log('errors', errors);

  if (success === true) {
    return (
      <p className="py-30">Thank you for submitting your Weil-McLain Product Registration!</p>
    )
  }

  if (success === false) {
    return (
      <p className="py-30">There was an error. Please refresh the page and try again.</p>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      {(success === null) && (
          <>
            <p className="mb-5">Please provide the information below to register your product.</p>

            <p className="mb-5">Should you have any questions, please contact Weil-McLain at<br/>
                1-800-654-2109, Option 4 to reach our Warranty department.</p>
          </>
        )
      }
      <Grid container spacing={2} className="my-10">
        <Grid item xs={12}>
          <Button onClick={() => {
            setIsHomeowner(true)
            setIsInstaller(false)
            setIsInstallerInfo(false)
            setShowProduct(false)
          }}>
            I am a homeowner registering my new product
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button onClick={() => {
            setIsInstaller(true)
            setIsInstallerInfo(true)
            setIsHomeowner(false)
            setShowProduct(true)
          }}>
            I am an installer registering a recent product installation
          </Button>
        </Grid>
      </Grid>
      {(isHomeowner === true) && (
        <div className="my-10">
          <p>Do you know the company installing your product?</p>
          <Button onClick={() => {
            setIsInstallerInfo(true)
            setShowProduct(true)
          }}>
            Yes
          </Button>
          <Button
            onClick={() => {
              setIsInstallerInfo(false)
              setShowProduct(true)
            }}
          >
            No
          </Button>
          </div>
      )}

      <form onKeyDown={handleKeyDown} onSubmit={handleSubmit(onSubmit)} id="xyzF" className="product-registration-form-form">
        {((isInstaller === true) || (isInstallerInfo)) && (
          <div className="installer-info mb-10">
            <h3>Find {isInstaller && "My"} Company</h3>
            {showCompanyName ? (
              <div className="w-full my-4">
                <TextField
                  className="w-full"
                  {...register("company_name")}
                  label="Company Name"
                  placeholder="Your Company Name"
                  type="text"
                  required
                  error={errors.company_name}
                />
                <Typography variant="inherit" color="textError">
                  {errors.company_name?.message}
                </Typography>
              </div>
            ) : (
              <div className="w-full my-4">
                <CompanyLookup
                  control={control}
                  label="Company (type to search)"
                  name="company"
                  placeholder="Begin typing a Company Name"
                  required
                  // onChange={handleCompanyChange}
                />
              </div>
            )}
            {(companyValue && !companyValue?.value?.address) && (
              <>
                {/* <div className="w-full my-4">
                  <Controller
                    as={InputMask}
                    control={control}
                    name="installer_phone"
                    render={({ field: { onChange, value } }) => (
                      <InputMask
                        mask="999-999-9999"
                        disabled={false}
                        maskChar=""
                        onChange={onChange}
                        value={value}
                      >
                        {(inputProps) =>
                          <TextField
                            {...inputProps}
                            className="w-full"
                            label="Installer Phone"
                            placeholder="555-555-5555"
                            type="tel"
                            required
                            error={errors.installer_phone}
                          />
                        }
                      </InputMask>
                    )}
                  />
                  <Typography variant="inherit" color="textError">
                    {errors.installer_phone?.message}
                  </Typography>
                </div> */}
                <div className="w-full my-4">
                  <AddressLookup
                    control={control}
                    label="Company Address (type to search)"
                    name="installer_address"
                    placeholder="Begin typing an address"
                    required
                    register={register}
                    setValue={setValue}
                    watchedValues={watchedValues}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {(showProduct === true) && (
          <>
            <h3>Product</h3>
            <div className="w-full my-4 mb-5">
              <CPLookup
                control={control}
                label="CP Number (type to search)"
                name="cp_number"
                required
                placeholder="e.g., CP1234567"
                error={errors.cp_number}
                setError={setError}
              />
              {cpValue && (
                <div className="cp-info" id="cp--info">
                  {cpInfo.length ? (
                    cpInfo.map(item => (
                      <div><span className="font-bold">{item.label}:</span> {item.value}</div>
                    ))
                  ) : (
                    "Please enter a valid CP Number (e.g. CP1234567)"
                  )}
                  <Typography variant="inherit" color="textError">
                    <p className="mttt-4">{errors.cp_number?.message}</p>
                  </Typography>
                </div>
              )}
            </div>

            <div className="w-full my-4 mb-5">
              <p className="mt-10 mb-5">Provide images of this installation.<br/>
                Include piping around or above the boiler or tank.</p>
                Image 1:
              <input
                {...register("image1File")}
                type="file"
                id="image1File"
                name="image1File"
                onChange={handleImageChange}
              />
              <input
                {...register("image1")}
                type="hidden"
                name="image1"
              />
              <Typography variant="inherit" color="textError">
                {errors.image1?.message}
              </Typography>
            </div>
            <div className="w-full my-4 mb-5">
              Image 2:
              <input
                {...register("image2File")}
                type="file"
                name="image2File"
                onChange={handleImageChange}
              />
              <input
                {...register("image2")}
                type="hidden"
                name="image2"
              />
            </div>
            <div className="w-full my-4 mb-10">
              Image 3:
              <input
                {...register("image3File")}
                type="file"
                name="image3File"
                onChange={handleImageChange}
              />
              <input
                {...register("image3")}
                type="hidden"
                name="image3"
              />
            </div>

            <h3>Installation Date</h3>
            <div className="w-full my-4 mb-10">
              <BasicDatePicker
                control={control}
                label="Installation Date"
                name="installation_date"
                placeholder="Installation Date"
                required
                isError={errors.installation_date}
              />
              <Typography variant="inherit" color="textError">
                {errors.installation_date?.message}
              </Typography>
            </div>

            <h3>Homeowner</h3>
            <div className="w-full my-4">
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    className="w-full"
                    {...register("homeowner_firstname")}
                    label="First Name"
                    placeholder="Jane"
                    required
                    error={errors.homeowner_firstname}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    className="w-full"
                    {...register("homeowner_lastname")}
                    label="Last Name"
                    placeholder="Smith"
                    required
                    error={errors.homeowner_lastname}
                  />
                </Grid>
              </Grid>
              <Typography variant="inherit" color="textError">
                {errors.homeowner_firstname?.message}
              </Typography>
              <Typography variant="inherit" color="textError">
                {errors.homeowner_lastname?.message}
              </Typography>
            </div>
            <div className="w-full my-4">
              <Controller
                as={InputMask}
                control={control}
                name="homeowner_phone"
                render={({ field: { onChange, value } }) => (
                  <InputMask
                    mask="999-999-9999"
                    disabled={false}
                    maskChar=""
                    onChange={onChange}
                    value={value}
                  >
                    {(inputProps) =>
                      <TextField
                        {...inputProps}
                        className="w-full"
                        label="Mobile number"
                        placeholder="555-555-5555"
                        type="tel"
                        required
                        error={errors.homeowner_phone}
                      />
                    }
                  </InputMask>
                )}
              />
              <Typography variant="inherit" color="textError">
                {errors.homeowner_phone?.message}
              </Typography>
            </div>
            <div className="w-full my-4 mb-10">
              <AddressLookup
                control={control}
                label="Address (type to search)"
                name="homeowner_address"
                placeholder="Begin typing an address"
                required
                register={register}
                setValue={setValue}
                watchedValues={watchedValues}
              />
              <Typography variant="inherit" color="textError">
                {errors.homeowner_address?.message}
              </Typography>
            </div>

            <h3>Confirmation</h3>
            <div className="w-full my-4 mb-10">
              <TextField
                className="w-full"
                {...register("homeowner_email")}
                label="Confirmation Email Address"
                placeholder="name@example.com"
                type="email"
                required
                error={errors.homeowner_email}
              />
              <Typography variant="inherit" color="textError">
                {errors.homeowner_email?.message}
              </Typography>
            </div>

            {(errors && Object.keys(errors).length !== 0)  && (
              <Typography variant="inherit" color="textError">
                Please fix errors before submitting.
              </Typography>
            )}

            <div className="my-10">
              <Button
                type="submit"
                disabled={errors && Object.keys(errors).length !== 0}
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  "Submit"
                )}
              </Button>
            </div>

            <div className="py-10" />
          </>
        )}


        <div>
          {/* register your input into the hook by invoking the "register" function */}
          {/* <input defaultValue="test" {...register("example")} /> */}

          {/* include validation with required or other standard HTML validation rules */}
          {/* <input {...register("exampleRequired", { required: true })} /> */}
          {/* errors will return when field validation fails  */}
          {/* {errors.exampleRequired && <span>This field is required</span>} */}
        </div>

        {/* <input type="submit" /> */}
      </form>
    </ThemeProvider>
  );
};

export default ProductRegistrationForm;
